import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LogoMark } from "@/components/Shell";
import { useAuth } from "@/context/AuthContext";
import { authErrorText, supabase } from "@/lib/supabase";

// Opened from the reset email (?token_hash=…&type=recovery): verify the link, then choose a new password.
export default function ResetPassword() {
  const verified = useRef(false);
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const invited = useLocation().state?.invited;
  const [ready, setReady] = useState(false);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (verified.current) return;
    verified.current = true;
    const params = new URLSearchParams(window.location.search);
    const tokenHash = params.get("token_hash");
    const code = params.get("code");
    window.history.replaceState(null, "", window.location.pathname);
    (async () => {
      let err = null;
      if (tokenHash) ({ error: err } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: "recovery" }));
      else if (code) ({ error: err } = await supabase.auth.exchangeCodeForSession(code));
      else if (!(await supabase.auth.getSession()).data.session) err = { message: "Open this page from the link in your reset email." };
      if (err) setError(authErrorText(err)); else setReady(true);
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const { error: err } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (err) return setError(authErrorText(err));
    const me = await refresh();
    navigate(me?.org ? "/home" : "/onboarding", { replace: true });
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-5 bg-background" data-testid="reset-password">
      <div className="max-w-md w-full space-y-6">
        <Link to="/" className="flex items-center gap-3"><LogoMark /><span className="font-display text-lg">LegacyStack AI</span></Link>
        <h1 className="font-display text-3xl sm:text-4xl">{invited ? "Welcome — choose a password." : "Choose a new password."}</h1>
        {ready ? (
          <form onSubmit={submit} className="space-y-3">
            <input required type="password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password (8+ characters)" autoComplete="new-password" data-testid="reset-password-input" className="w-full h-12 rounded-xl border border-border bg-background px-4 text-sm focus:outline-none focus:ring-2 focus:ring-gold/60" />
            {error && <p className="text-sm text-destructive" role="alert">{error}</p>}
            <button type="submit" disabled={busy} className="btn-gold w-full h-12" data-testid="reset-submit">{busy ? "One moment…" : "Save password"}</button>
          </form>
        ) : error ? (
          <div className="space-y-3"><p className="text-sm text-destructive" role="alert" data-testid="reset-error">{error}</p><Link to="/auth/forgot" className="btn-outline inline-flex">Send a new link</Link></div>
        ) : (
          <p className="text-sm text-muted-foreground">Checking your link…</p>
        )}
      </div>
    </div>
  );
}
